import React, { useState, useEffect } from "react";
import axios from "axios";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Link, useNavigate } from "react-router-dom";

const UserFeed = () => {
  const [taggedPhotos, setTaggedPhotos] = useState([]);
  const [feedPhotos, setFeedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [viewType, setViewType] = useState("tagged"); // 'tagged' or 'feed'
  const [error, setError] = useState(null);
  const [currentPhotoIndices, setCurrentPhotoIndices] = useState({});
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectedAlbums, setSelectedAlbums] = useState([]);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const navigate = useNavigate();

  // Initial load of both data sets
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load both data sets in parallel
        const [taggedResponse, feedResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_DATABASE_URL}/api/photos/tagged`),
          axios.get(`${process.env.REACT_APP_DATABASE_URL}/api/photos/feed`),
        ]);

        console.log("Tagged Response:", taggedResponse.data);
        setTaggedPhotos(taggedResponse.data || []);
        setFeedPhotos(feedResponse.data || []);

        // Initialize currentPhotoIndices with 0 for each group
        const initialIndices = {};
        taggedResponse.data.forEach((group, idx) => {
          initialIndices[`tagged-${idx}`] = 0;
        });
        feedResponse.data.forEach((group, idx) => {
          initialIndices[`feed-${idx}`] = 0;
        });
        setCurrentPhotoIndices(initialIndices);

        setInitialLoadComplete(true);
      } catch (err) {
        console.error("Error loading photos:", err);
        setError("Failed to load photos. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, []);

  // Navigate to previous photo in a group
  const handlePrevPhoto = (groupType, groupIndex) => {
    const groupKey = `${groupType}-${groupIndex}`;
    const group =
      groupType === "tagged"
        ? taggedPhotos[groupIndex]
        : feedPhotos[groupIndex];

    const photoCount = group.photos?.length || 0;
    if (photoCount <= 1) return;

    setCurrentPhotoIndices((prev) => ({
      ...prev,
      [groupKey]: (prev[groupKey] - 1 + photoCount) % photoCount,
    }));
  };

  // Navigate to next photo in a group
  const handleNextPhoto = (groupType, groupIndex) => {
    const groupKey = `${groupType}-${groupIndex}`;
    const group =
      groupType === "tagged"
        ? taggedPhotos[groupIndex]
        : feedPhotos[groupIndex];

    const photoCount = group.photos?.length || 0;
    if (photoCount <= 1) return;

    setCurrentPhotoIndices((prev) => ({
      ...prev,
      [groupKey]: (prev[groupKey] + 1) % photoCount,
    }));
  };

  // Refresh data periodically or when needed
  const refreshData = async (type) => {
    try {
      if (type === "tagged" || type === "both") {
        const response = await axios.get(
          `${process.env.REACT_APP_DATABASE_URL}/api/photos/tagged`
        );
        console.log("Tagged Response:", response.data);
        setTaggedPhotos(response.data || []);
      }

      if (type === "feed" || type === "both") {
        const response = await axios.get(
          `${process.env.REACT_APP_DATABASE_URL}/api/photos/feed`
        );
        setFeedPhotos(response.data || []);
      }
    } catch (err) {
      console.error(`Error refreshing ${type} photos:`, err);
      // Don't set error state here to avoid disrupting the UI
    }
  };

  // Optional: Refresh active view data every 30 seconds
  useEffect(() => {
    if (!initialLoadComplete) return;

    const interval = setInterval(() => {
      refreshData(viewType);
    }, 30000);

    return () => clearInterval(interval);
  }, [viewType, initialLoadComplete]);

  // Handle album selection in download modal
  const handleAlbumSelect = (album, checked) => {
    if (checked) {
      setSelectedAlbums([...selectedAlbums, album]);
      // Add all photos in this album to selected photos
      const albumPhotoIds = album.photos.map((photo) => photo.id);
      setSelectedPhotos((prev) => [...new Set([...prev, ...albumPhotoIds])]);
    } else {
      setSelectedAlbums(selectedAlbums.filter((a) => a !== album));
      // Remove all photos in this album from selected photos
      const albumPhotoIds = album.photos.map((photo) => photo.id);
      setSelectedPhotos((prev) =>
        prev.filter((id) => !albumPhotoIds.includes(id))
      );
    }
  };

  // Handle individual photo selection
  const handlePhotoSelect = (photoId, checked) => {
    if (checked) {
      setSelectedPhotos([...selectedPhotos, photoId]);
    } else {
      setSelectedPhotos(selectedPhotos.filter((id) => id !== photoId));
      // Also uncheck the album if this photo was part of a selected album
      const containingAlbum = getDisplayPhotos().find((album) =>
        album.photos.some((photo) => photo.id === photoId)
      );
      if (containingAlbum && selectedAlbums.includes(containingAlbum)) {
        setSelectedAlbums(
          selectedAlbums.filter((album) => album !== containingAlbum)
        );
      }
    }
  };

  // Handle bulk download
  const handleBulkDownload = async () => {
    if (selectedPhotos.length === 0) return;

    // Can only download tagged photos
    if (viewType !== "tagged") {
      alert("You can only download photos you're tagged in");
      return;
    }

    setDownloadingZip(true);
    setDownloadProgress(0);

    try {
      const zip = new JSZip();

      // Download each selected photo and add to zip
      let completed = 0;
      let errors = 0;

      // Create an array of promises for all downloads
      const downloadPromises = selectedPhotos.map(async (photoId) => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_DATABASE_URL}/api/photos/${photoId}/download`,
            {
              responseType: "blob",
            }
          );

          zip.file(`photo-${photoId}.jpg`, response.data);
          completed++;
        } catch (error) {
          errors++;
          console.error(`Error downloading photo ${photoId}:`, error);
        }

        setDownloadProgress(
          Math.round(((completed + errors) / selectedPhotos.length) * 100)
        );
      });

      await Promise.all(downloadPromises);

      if (completed > 0) {
        // Generate and download the zip file
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, "my-photos.zip");
      } else {
        alert(
          "No photos could be downloaded. You can only download photos you're tagged in."
        );
      }
    } catch (error) {
      console.error("Error creating zip file:", error);
    } finally {
      setDownloadingZip(false);
      // Close modal after download
      setTimeout(() => {
        setShowDownloadModal(false);
        setSelectedPhotos([]);
        setSelectedAlbums([]);
      }, 1000);
    }
  };

  const getDisplayPhotos = () => {
    return viewType === "tagged" ? taggedPhotos || [] : feedPhotos || [];
  };

  const getPhotoCount = () => {
    // For tagged photos, count individual photos
    let count = 0;
    getDisplayPhotos().forEach((group) => {
      if (group.photos && Array.isArray(group.photos)) {
        count += group.photos.length;
      }
    });
    return count;
  };

  // Smooth toggle switch with subtle animations
  const ToggleSwitch = () => (
    <div className="flex items-center justify-center my-6">
      <div className="relative">
        <div className="w-64 h-12 flex items-center bg-gray-200 rounded-full p-1 relative shadow-sm">
          {/* Slider Background with smoother transition */}
          <div
            className={`absolute inset-y-1 transition-all duration-500 ease-in-out rounded-full bg-gradient-to-r from-purple-600 to-pink-600 shadow-md ${
              viewType === "tagged"
                ? "left-1 right-[calc(50%+2px)]"
                : "left-[calc(50%+2px)] right-1"
            }`}
          ></div>

          {/* Tagged Button */}
          <button
            className={`flex-1 h-full flex justify-center items-center rounded-full z-10 transition-colors duration-300 ${
              viewType === "tagged" ? "text-white font-medium" : "text-gray-700"
            }`}
            onClick={() => {
              if (viewType !== "tagged") setViewType("tagged");
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
            <span className="relative">Tagged</span>
          </button>

          {/* Feed Button */}
          <button
            className={`flex-1 h-full flex justify-center items-center rounded-full z-10 transition-colors duration-300 ${
              viewType === "feed" ? "text-white font-medium" : "text-gray-700"
            }`}
            onClick={() => {
              if (viewType !== "feed") setViewType("feed");
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
            </svg>
            <span className="relative">Feed</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Download Modal Component
  const DownloadModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-bold text-gray-900">Download Photos</h3>
          <button
            onClick={() => {
              setShowDownloadModal(false);
              setSelectedPhotos([]);
              setSelectedAlbums([]);
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-6">
            {getDisplayPhotos().map((album, albumIndex) => (
              <div
                key={albumIndex}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Album Header */}
                <div className="bg-gray-50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedAlbums.includes(album)}
                        onChange={(e) =>
                          handleAlbumSelect(album, e.target.checked)
                        }
                        className="h-5 w-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300 mr-3"
                      />
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">
                          {album.albumName || "Unnamed Album"}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {album.photos?.length || 0} photos
                          {album.location && ` • ${album.location}`}
                          {album.date &&
                            ` • ${new Date(album.date).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {album.photos?.filter((photo) =>
                        selectedPhotos.includes(photo.id)
                      ).length || 0}{" "}
                      selected
                    </div>
                  </div>
                </div>

                {/* Photos Grid */}
                <div className="p-4">
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                    {album.photos?.map((photo) => (
                      <div key={photo.id} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden">
                          <img
                            src={photo.url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all">
                            <div className="absolute top-2 right-2">
                              <input
                                type="checkbox"
                                checked={selectedPhotos.includes(photo.id)}
                                onChange={(e) =>
                                  handlePhotoSelect(photo.id, e.target.checked)
                                }
                                className="h-4 w-4 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer with download button */}
        <div className="border-t p-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {selectedPhotos.length} photos selected from{" "}
              {selectedAlbums.length} albums
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDownloadModal(false);
                  setSelectedPhotos([]);
                  setSelectedAlbums([]);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDownload}
                disabled={selectedPhotos.length === 0 || downloadingZip}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {downloadingZip ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Downloading... {downloadProgress}%
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Download {selectedPhotos.length} Photos
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading && !initialLoadComplete) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="mb-4">
            <svg
              className="animate-spin h-12 w-12 text-purple-600 mx-auto"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <p className="text-gray-600 text-lg">Loading your photos...</p>
        </div>
      </div>
    );
  }

  if (error && !initialLoadComplete) {
    return (
      <div className="max-w-5xl mx-auto p-6 pt-10">
        <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
          <div className="mb-6 text-red-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Error Loading Photos
          </h2>
          <p className="text-gray-600 max-w-md mx-auto mb-6">{error}</p>
          <button
            onClick={() => refreshData("both")}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-full shadow-md hover:shadow-lg transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const displayPhotos = getDisplayPhotos();
  const isEmpty = !displayPhotos || displayPhotos.length === 0;

  return (
    <div className="max-w-6xl mx-auto p-4 pt-6">
      <ToggleSwitch />

      {initialLoadComplete && isEmpty ? (
        <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
          <div className="mb-6">
            <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {viewType === "tagged"
              ? "You haven't been tagged in any photos yet"
              : "Your friends haven't shared any photos yet"}
          </h2>
          <p className="text-gray-600 max-w-md mx-auto mb-8">
            {viewType === "tagged"
              ? "When friends tag you in their uploads, they'll appear here"
              : "Photos your friends mark as 'Show on Profile' will appear here"}
          </p>
          {viewType === "tagged" && (
            <Link
              to="/upload"
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-full shadow-md hover:shadow-lg transition-all"
            >
              Upload Your Own Photos
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Header with download action */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {viewType === "tagged"
                  ? "Photos You're Tagged In"
                  : "Your Friends' Photos"}
              </h1>

              {viewType === "tagged" && getPhotoCount() > 0 && (
                <button
                  onClick={() => setShowDownloadModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-full shadow-md hover:shadow-lg transition-all flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download Photos
                </button>
              )}
            </div>
          </div>

          {/* Content area with fade transition */}
          <div
            className="transition-opacity duration-300"
            style={{ opacity: initialLoadComplete ? 1 : 0.6 }}
          >
            {/* Album view with clear separation */}
            <div className="space-y-16">
              {displayPhotos.map((group, groupIndex) => {
                // Skip groups without photos
                if (
                  !group.photos ||
                  !Array.isArray(group.photos) ||
                  group.photos.length === 0
                ) {
                  return null;
                }

                const groupType = viewType;
                const groupKey = `${groupType}-${groupIndex}`;
                const currentIndex = currentPhotoIndices[groupKey] || 0;
                const currentPhoto = group.photos[currentIndex];

                // Skip if no current photo
                if (!currentPhoto) return null;

                return (
                  <div
                    key={groupIndex}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-100 hover:border-purple-200 transition-all duration-300"
                  >
                    {/* Album Header with clear visual separation */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 border-b-4 border-purple-200">
                      <div className="flex justify-between items-start">
                        <div className="w-full">
                          {/* Album Title with visual emphasis */}
                          <div className="px-4 py-2">
                            <div className="flex items-center mb-4"
                            onClick={() => navigate(`/profile/${group.uploader.id}`)}>
                              <div className="h-10 w-10 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden">
                                {group.uploader.profilePhotoUrl ? (
                                  <img
                                    src={group.uploader.profilePhotoUrl}
                                    alt={group.uploader.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span className="text-lg font-medium text-purple-700">
                                    {group.uploader.name?.charAt(0)?.toUpperCase() || "?"}
                                  </span>
                                )}
                              </div>
                              <div className="ml-3">
                                <span className="text-base font-medium text-gray-800">
                                  {group.uploader.name || "User"}
                                </span>
                                {/* Optional: still show album title below uploader */}
                                <h2 className="text-2xl font-bold text-gray-900">
                                  {group.albumName || "Unnamed Album"}
                                </h2>
                              </div>
                            </div>
                          </div>


                          
                          {/* Album details */}
                          <div className="flex flex-wrap items-center gap-4 text-sm">
                            <div className="flex items-center bg-white px-3 py-2 rounded-lg shadow-sm">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 mr-2 text-purple-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              <span className="font-medium text-gray-700">
                                {new Date(group.date).toLocaleDateString(
                                  undefined,
                                  {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  }
                                )}
                              </span>
                            </div>

                            <div className="flex items-center bg-white px-3 py-2 rounded-lg shadow-sm">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 mr-2 text-purple-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              <span className="font-medium text-gray-700">
                                {group.photos.length}{" "}
                                {group.photos.length === 1 ? "photo" : "photos"}
                              </span>
                            </div>

                            {group.location && (
                              <div className="flex items-center bg-white px-3 py-2 rounded-lg shadow-sm">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 mr-2 text-purple-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                                <span className="font-medium text-gray-700">
                                  {group.location}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Main photo with navigation arrows */}
                    <div className="relative bg-black">
                      <img
                        src={currentPhoto.url}
                        alt="Photo"
                        className="w-full h-96 object-contain"
                      />

                      {/* Photo counter indicator */}
                      {group.photos.length > 1 && (
                        <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                          <div className="bg-black bg-opacity-50 text-white text-sm px-3 py-1 rounded-full">
                            {currentIndex + 1} / {group.photos.length}
                          </div>
                        </div>
                      )}

                      {/* Navigation arrows - only if more than one photo */}
                      {group.photos.length > 1 && (
                        <>
                          {/* Left arrow */}
                          <button
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-3 transition-all"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrevPhoto(groupType, groupIndex);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 19l-7-7 7-7"
                              />
                            </svg>
                          </button>

                          {/* Right arrow */}
                          <button
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-3 transition-all"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNextPhoto(groupType, groupIndex);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>

                    {/* Photo information and thumbnail row */}
                    <div className="p-4">
                      <div className="flex justify-between items-center mb-4">
                        <div className="text-sm text-gray-600 flex items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                            />
                          </svg>
                          {currentPhoto.tags
                            ? `${currentPhoto.tags.length} people tagged`
                            : `${currentPhoto.tagCount || 0} people tagged`}
                        </div>
                      </div>

                      {/* Thumbnail row - when there's more than one photo */}
                      {group.photos.length > 1 && (
                        <div className="flex overflow-x-auto space-x-2 py-2">
                          {group.photos.map((photo, photoIndex) => (
                            <div
                              key={photo.id}
                              className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden cursor-pointer border-2 ${
                                photoIndex === currentIndex
                                  ? "border-purple-500"
                                  : "border-transparent hover:border-purple-200"
                              }`}
                              onClick={() => {
                                setCurrentPhotoIndices((prev) => ({
                                  ...prev,
                                  [groupKey]: photoIndex,
                                }));
                              }}
                            >
                              <img
                                src={photo.url}
                                alt={`Thumbnail ${photoIndex + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Download Modal */}
      {showDownloadModal && <DownloadModal />}

      {/* Optional refresh button */}
      <div className="fixed bottom-6 right-6">
        <button
          onClick={() => refreshData("both")}
          className="flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg text-purple-600 hover:text-purple-800 transition-all hover:shadow-xl"
          title="Refresh photos"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default UserFeed;
